// components/pinned-projects/PinnedProjectsButton.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { BsPinAngleFill } from "react-icons/bs";

import PinnedProjectTree, {
  pinnedProjectUrl,
} from "./PinnedProjectTree";

import "../collab-projects/collab-projects.scss";

export default function PinnedProjectsButton() {
  const currentUser = useSelector(
    (state) => state.user.currentUser,
  );

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);

  const anchor = useRef(null);
  const panel = useRef(null);
  const timer = useRef(null);

  const { pathname } = useLocation();

  const selected = (currentUser?.favoriteStations || []).some(
    (project) =>
      pathname.startsWith(
        pinnedProjectUrl(project).slice(0, -1),
      ),
  );

  const cancelClose = () =>
    window.clearTimeout(timer.current);

  const close = useCallback(() => {
    window.clearTimeout(timer.current);
    setOpen(false);
    setPosition(null);
  }, []);

  const leave = () => {
    cancelClose();
    timer.current = window.setTimeout(close, 160);
  };

  useEffect(() => {
    close();
  }, [pathname, close]);

  useEffect(
    () => () => window.clearTimeout(timer.current),
    [],
  );

  useLayoutEffect(() => {
    if (!open || !anchor.current || !panel.current) return;

    const place = () => {
      const button = anchor.current.getBoundingClientRect();
      const rect = panel.current.getBoundingClientRect();

      const left = Math.max(
        8,
        Math.min(
          button.right + 10,
          window.innerWidth - rect.width - 8,
        ),
      );

      const top = Math.max(
        8,
        Math.min(
          button.top - 4,
          window.innerHeight - rect.height - 8,
        ),
      );

      setPosition({
        left,
        top,
        tabLeft: button.left - 4,
        tabTop: button.top - 4,
        connected:
          Math.abs(left - (button.right + 10)) < 1,
      });
    };

    place();

    const resize = new ResizeObserver(place);
    resize.observe(anchor.current);
    resize.observe(panel.current);

    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);

    return () => {
      resize.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  return (
    <>
      <div
        ref={anchor}
        className="collab-launcher"
        onPointerEnter={(event) => {
          if (event.pointerType !== "mouse") return;

          cancelClose();
          setOpen(true);
        }}
        onPointerLeave={leave}
      >
        <button
          type="button"
          className={`pinned-launcher__button${
            open ? " is-open" : ""
          }${selected ? " is-selected" : ""}`}
          aria-label="Pinned Projects"
          aria-expanded={open}
          aria-controls={
            open ? "pinned-projects-panel" : undefined
          }
          onFocus={() => {
            cancelClose();
            setOpen(true);
          }}
        >
          <BsPinAngleFill  />
        </button>
      </div>

      {open &&
        createPortal(
          <>
            {position?.connected && (
              <span
                className="collab-launcher__portal-tab"
                aria-hidden="true"
                style={{
                  left: position.tabLeft,
                  top: position.tabTop,
                }}
              >
                <BsPinAngleFill />
              </span>
            )}

            {position?.connected && (
              <svg
                className="collab-panel__connector"
                aria-hidden="true"
                style={{
                  left: position.tabLeft + 32,
                  top: position.tabTop + 32,
                }}
                width={
                  position.left +
                  13 -
                  (position.tabLeft + 32)
                }
                height="1"
              >
                <path
                  d={`M 0 0 C 12 0 12 ${
                    position.top +
                    22 -
                    position.tabTop -
                    32
                  } ${
                    position.left +
                    13 -
                    position.tabLeft -
                    32
                  } ${
                    position.top +
                    22 -
                    position.tabTop -
                    32
                  }`}
                />
              </svg>
            )}

            <section
              ref={panel}
              id="pinned-projects-panel"
              className="collab-panel"
              aria-label="Pinned Projects"
              style={{
                left: position?.left ?? 64,
                top: position?.top ?? 8,
                visibility: position
                  ? "visible"
                  : "hidden",
              }}
              onPointerEnter={cancelClose}
              onPointerLeave={leave}
            >
              <header>
                <strong>
                  <span
                    className="collab-panel__dot"
                    aria-hidden="true"
                  />
                  Pinned Projects
                </strong>
              </header>

              <div className="collab-panel__body">
                <PinnedProjectTree
                  heading={false}
                  onNavigate={close}
                />
              </div>
            </section>
          </>,
          document.body,
        )}
    </>
  );
}